import {
  segmentsQuery,
  templatesQuery,
  evaluateSegment,
  previewTemplateForMember,
  useCreateSendMutation,
  type SegmentEvaluationResult,
  type EmailTemplate,
  type Segment,
} from "@/api/campaigns";
import { StyledLink } from "@/components/link/StyledLink";
import { Button } from "@/components/ui/Button";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { seo } from "@/utils/seo";
import { Box, Field, Flex, Label, Select, Text, toast } from "@givenwell/components";
import { Input } from "@/components/ui/Input";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/campaigns/sends/new")({
  component: CreateSend,
  head: () => ({ meta: seo({ title: "New Send" }) }),
});

type WizardStep = "segment" | "template" | "preview" | "schedule";

const STEPS: { key: WizardStep; label: string }[] = [
  { key: "segment", label: "1. Segment" },
  { key: "template", label: "2. Template" },
  { key: "preview", label: "3. Preview" },
  { key: "schedule", label: "4. Schedule" },
];

function CreateSend() {
  const navigate = useNavigate();
  const [step, setStep] = useState<WizardStep>("segment");

  // Wizard state
  const [selectedSegmentId, setSelectedSegmentId] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [sendName, setSendName] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [sendImmediately, setSendImmediately] = useState(true);
  const [emailType, setEmailType] = useState<"campaign" | "transactional">("campaign");
  const [trackOpens, setTrackOpens] = useState(true);

  // Evaluation results
  const [evalResult, setEvalResult] = useState<SegmentEvaluationResult | null>(null);
  const [evaluating, setEvaluating] = useState(false);

  // Preview
  const [previewMemberId, setPreviewMemberId] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Queries
  const segmentsQ = useQuery(segmentsQuery());
  const templatesQ = useQuery(templatesQuery());
  const createMut = useCreateSendMutation();

  const segments = segmentsQ.data || [];
  const templates = templatesQ.data || [];
  const selectedSegment = segments.find(s => s.segment_id === selectedSegmentId);
  const selectedTemplate = templates.find(t => t.email_template_id === selectedTemplateId);

  useEffect(() => {
    if (selectedTemplate) {
      if (selectedTemplate.email_type) {
        setEmailType(selectedTemplate.email_type);
      }
      setTrackOpens(selectedTemplate.track_opens ?? true);
    }
  }, [selectedTemplate]);

  async function handleEvaluate() {
    if (!selectedSegmentId) return;
    setEvaluating(true);
    try {
      const result = await evaluateSegment(selectedSegmentId);
      setEvalResult(result);
      const firstMember = result.members[0];
      if (firstMember) {
        setPreviewMemberId(firstMember.member_id);
      }
    } catch {
      toast.error("Failed to evaluate segment");
    } finally {
      setEvaluating(false);
    }
  }

  async function handlePreview(memberId: string) {
    if (!selectedTemplateId || !memberId) return;
    setLoadingPreview(true);
    try {
      const result = await previewTemplateForMember(selectedTemplateId, memberId);
      setPreviewHtml(result.html);
    } catch {
      setPreviewHtml("<p>Preview failed.</p>");
    } finally {
      setLoadingPreview(false);
    }
  }

  function handleCreate() {
    if (!selectedTemplateId || !selectedSegmentId || !sendName) return;

    createMut.mutate(
      {
        email_template_id: selectedTemplateId,
        segment_id: selectedSegmentId,
        name: sendName,
        scheduled_at: sendImmediately ? undefined : scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
        email_type: emailType,
        track_opens: trackOpens,
      },
      {
        onSuccess: data => {
          toast.success("Send created");
          navigate({
            to: "/campaigns/sends/$sendId",
            params: { sendId: data.email_send_id },
          });
        },
      },
    );
  }

  const canProceedFromSegment = !!evalResult && evalResult.count > 0;
  const canProceedFromTemplate = !!selectedTemplateId;

  return (
    <PageWrapper
      title="New Send"
      rightContent={
        <Button variant="plain" color="neutral" onClick={() => navigate({ to: "/campaigns/sends" })}>
          Cancel
        </Button>
      }
    >
      {/* Step indicators */}
      <Flex css={{ gap: 4, mb: 24 }}>
        {STEPS.map(s => (
          <Box
            key={s.key}
            css={{
              px: 16,
              py: 8,
              rounded: 8,
              fontScale: "sm",
              fontWeight: step === s.key ? 600 : 400,
              color: step === s.key ? "white" : colors.text2,
              background: step === s.key ? colors.navy : colors.cream,
              cursor: "pointer",
            }}
            onClick={() => {
              // Only allow navigating to completed steps
              const stepIndex = STEPS.findIndex(st => st.key === s.key);
              const currentIndex = STEPS.findIndex(st => st.key === step);
              if (stepIndex <= currentIndex) setStep(s.key);
            }}
          >
            {s.label}
          </Box>
        ))}
      </Flex>

      {/* Step 1: Segment */}
      {step === "segment" && (
        <Box css={{ background: "white", p: 16, rounded: 12, spaceY: 16 }}>
          <Field>
            <Label>Select a saved segment</Label>
            <Select
              value={selectedSegmentId}
              onValueChange={v => {
                setSelectedSegmentId(v);
                setEvalResult(null);
              }}
            >
              <option value="">Choose segment...</option>
              {segments.map((s: Segment) => (
                <option key={s.segment_id} value={s.segment_id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>

          {evalResult && (
            <Box>
              <Text css={{ fontWeight: 600, mb: 8 }}>
                {evalResult.count} member{evalResult.count !== 1 && "s"} matched
              </Text>
              <Box css={{ maxHeight: 300, overflow: "auto", border: `1px solid ${colors.outline}`, rounded: 8 }}>
                {evalResult.members.map(m => (
                  <Flex key={m.member_id} css={{ py: 6, px: 12, borderBottom: `1px solid ${colors.outline}` }}>
                    <Text css={{ fontScale: "sm" }}>
                      <StyledLink to="/admin/members/$memberId" params={{ memberId: m.member_id }} target="_blank">
                        {m.given_name} {m.family_name}
                      </StyledLink>{" "}
                      — {m.email}
                    </Text>
                  </Flex>
                ))}
              </Box>
            </Box>
          )}

          <Flex css={{ items: "center", mt: 16, gap: 8 }} style={{ width: "100%" }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleEvaluate}
              loading={evaluating}
              disabled={!selectedSegmentId}
            >
              Evaluate
            </Button>
            <Button
              variant="tonal"
              color="primary"
              onClick={() => setStep("template")}
              disabled={!canProceedFromSegment}
              style={{ marginLeft: "auto" }}
            >
              Next: Template
            </Button>
          </Flex>
        </Box>
      )}

      {/* Step 2: Template */}
      {step === "template" && (
        <Box css={{ background: "white", p: 16, rounded: 12, spaceY: 16 }}>
          <Field>
            <Label>Select an email template</Label>
            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
              <option value="">Choose template...</option>
              {templates.map((t: EmailTemplate) => (
                <option key={t.email_template_id} value={t.email_template_id}>
                  {t.name} — {t.subject}
                </option>
              ))}
            </Select>
          </Field>

          {selectedTemplate && (
            <Box css={{ border: `1px solid ${colors.outline}`, rounded: 8, p: 12, mt: 12 }}>
              <Text css={{ fontWeight: 600, mb: 4 }}>Subject: {selectedTemplate.subject}</Text>
              <Text css={{ fontScale: "sm", color: colors.text2, whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
                {selectedTemplate.markdown_body.slice(0, 300)}
                {selectedTemplate.markdown_body.length > 300 && "..."}
              </Text>
            </Box>
          )}

          <Flex css={{ items: "center", mt: 16, gap: 8 }} style={{ width: "100%" }}>
            <Button variant="plain" color="neutral" onClick={() => setStep("segment")}>
              Back
            </Button>
            <Button
              variant="tonal"
              color="primary"
              onClick={() => {
                setStep("preview");
                if (previewMemberId) handlePreview(previewMemberId);
              }}
              disabled={!canProceedFromTemplate}
              style={{ marginLeft: "auto" }}
            >
              Next: Preview
            </Button>
          </Flex>
        </Box>
      )}

      {/* Step 3: Preview */}
      {step === "preview" && (
        <Box css={{ background: "white", p: 16, rounded: 12, spaceY: 16 }}>
          <Flex css={{ gap: 12, items: "center" }}>
            <Text css={{ fontScale: "sm" }}>Preview as:</Text>
            <Select
              css={{ flex: 1, maxWidth: 400 }}
              value={previewMemberId}
              onValueChange={v => {
                setPreviewMemberId(v);
                handlePreview(v);
              }}
            >
              {(evalResult?.members || []).map(m => (
                <option key={m.member_id} value={m.member_id}>
                  {m.given_name} {m.family_name} — {m.email}
                </option>
              ))}
            </Select>
          </Flex>

          {loadingPreview ? (
            <Text css={{ color: colors.text2 }}>Loading preview...</Text>
          ) : previewHtml ? (
            <Box
              css={{
                border: `1px solid ${colors.outline}`,
                rounded: 8,
                p: 16,
                maxHeight: 500,
                overflow: "auto",
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </Box>
          ) : null}

          <Flex css={{ items: "center", mt: 16, gap: 8 }} style={{ width: "100%" }}>
            <Button variant="plain" color="neutral" onClick={() => setStep("template")}>
              Back
            </Button>
            <Button variant="tonal" color="primary" onClick={() => setStep("schedule")} style={{ marginLeft: "auto" }}>
              Next: Schedule
            </Button>
          </Flex>
        </Box>
      )}

      {/* Step 4: Schedule */}
      {step === "schedule" && (
        <Box css={{ background: "white", p: 16, rounded: 12, spaceY: 16 }}>
          <Field>
            <Label>Send name</Label>
            <Input value={sendName} onValueChange={setSendName} placeholder="e.g. January newsletter" />
          </Field>

          <Field>
            <Label>Email Type</Label>
            <select
              value={emailType}
              onChange={e => setEmailType(e.target.value as "campaign" | "transactional")}
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: 14,
                borderRadius: 8,
                border: `1px solid ${colors.outline}`,
                background: "white",
              }}
            >
              <option value="campaign">Campaign</option>
              <option value="transactional">Transactional</option>
            </select>
            <Text css={{ fontSize: 12, color: colors.text2, mt: 4 }}>
              {emailType === "campaign"
                ? "Sent from the campaign address and includes an unsubscribe footer."
                : "Sent from the main address with no unsubscribe footer. Use for receipts, notifications, and account emails."}
            </Text>
          </Field>

          <Field>
            <Label>Track Opens</Label>
            <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={trackOpens} onChange={e => setTrackOpens(e.target.checked)} />
              <Text css={{ fontScale: "sm" }}>Enable open tracking</Text>
            </label>
            <Text css={{ fontSize: 12, color: colors.text2, mt: 4 }}>
              Pre-filled from template default. When disabled, opens will not be tracked for this send.
            </Text>
          </Field>

          <Flex css={{ gap: 12, items: "center" }}>
            <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
              <input type="radio" checked={sendImmediately} onChange={() => setSendImmediately(true)} />
              <Text css={{ fontScale: "sm" }}>Send immediately</Text>
            </label>
            <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
              <input type="radio" checked={!sendImmediately} onChange={() => setSendImmediately(false)} />
              <Text css={{ fontScale: "sm" }}>Schedule for later</Text>
            </label>
          </Flex>

          {!sendImmediately && (
            <Field>
              <Label>Scheduled date/time</Label>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={e => setScheduledAt(e.target.value)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: `1px solid ${colors.outline}`,
                  fontSize: 14,
                }}
              />
            </Field>
          )}

          <Box css={{ border: `1px solid ${colors.outline}`, rounded: 8, p: 12, mt: 12, background: colors.cream }}>
            <Text css={{ fontWeight: 600, mb: 8 }}>Summary</Text>
            <Text css={{ fontScale: "sm" }}>Template: {selectedTemplate?.name}</Text>
            <Text css={{ fontScale: "sm" }}>Segment: {selectedSegment?.name}</Text>
            <Text css={{ fontScale: "sm" }}>Recipients: {evalResult?.count ?? 0}</Text>
            <Text css={{ fontScale: "sm" }}>Email Type: {emailType === "campaign" ? "Campaign" : "Transactional"}</Text>
            <Text css={{ fontScale: "sm" }}>Track Opens: {trackOpens ? "Yes" : "No"}</Text>
            <Text css={{ fontScale: "sm" }}>Timing: {sendImmediately ? "Immediately" : scheduledAt || "Not set"}</Text>
          </Box>

          <Flex css={{ items: "center", mt: 16, gap: 8 }} style={{ width: "100%" }}>
            <Button variant="plain" color="neutral" onClick={() => setStep("preview")}>
              Back
            </Button>
            <Button
              variant="tonal"
              color="primary"
              onClick={handleCreate}
              loading={createMut.isPending}
              loadingText="Creating..."
              disabled={!sendName.trim()}
              style={{ marginLeft: "auto" }}
            >
              {sendImmediately ? "Send Now" : "Schedule Send"}
            </Button>
          </Flex>
        </Box>
      )}
    </PageWrapper>
  );
}
